import type { GameScene } from '../scenes/GameScene';
import type { TouchSnapshot } from '../game/InputController';
import { gameState } from '../state/GameState';

export interface DebugBridge {
  getBarState(): { leftAnchorY: number; rightAnchorY: number };
  getBallState(): { x: number; y: number; speed: number };
  getLevel(): number;
  getBest(): number;
  getLives(): number;
  setLives(n: number): void;
  forceGameOver(): void;
  placeBallAtTarget(): void;
  placeBallAtTrap(): void;
  activeScene(): string | null;
  inspectHoles(): { type: string; poolSize: number } | null;
  starfieldHolesActive(): boolean;
  jumpToLevel(level: number): void;
  triggerTrip(type: 'invert' | 'aberration' | 'acid' | 'vignette'): void;
  getTokens(): number;
  setTokens(n: number): void;
  openMachine(): void;
  getActiveTouchSnapshots(): TouchSnapshot[];
}

export function attachDebugBridge(scene: GameScene): void {
  const enable = (import.meta as ImportMeta).env?.DEV || new URLSearchParams(location.search).has('debug');
  if (!enable) return;

  const bridge: DebugBridge = {
    getBarState: () => scene.debugGetBarState(),
    getBallState: () => scene.debugGetBallState(),
    getLevel: () => scene.debugGetLevel(),
    getBest: () => scene.debugGetBest(),
    getLives: () => scene.debugGetLives(),
    setLives: (n: number) => scene.debugSetLives(n),
    forceGameOver: () => scene.debugForceGameOver(),
    placeBallAtTarget: () => scene.debugPlaceBallAtTarget(),
    placeBallAtTrap: () => scene.debugPlaceBallAtTrap(),
    activeScene: () => scene.scene.manager.getScenes(true)[0]?.scene.key ?? null,
    inspectHoles: () => scene.debugInspectHoles(),
    starfieldHolesActive: () => scene.debugStarfieldHolesActive(),
    jumpToLevel: (level: number) => scene.debugJumpToLevel(level),
    triggerTrip: (type) => scene.debugTriggerTrip(type),
    getTokens: () => gameState.wallet.count,
    setTokens: (n: number) => {
      // Direct setter for tests. Spend down or grant up to reach n.
      const current = gameState.wallet.count;
      if (n > current) gameState.wallet.grant(n - current);
      else while (gameState.wallet.count > n) gameState.wallet.spend();
      scene.game.events.emit('lavascend:tokens');
    },
    openMachine: () => {
      if (!scene.scene.isActive('Title')) return;
      scene.scene.manager.getScene('Title')?.scene.launch('TokenMachine', {});
    },
    getActiveTouchSnapshots: () => scene.getJoystickSnapshots()
  };

  (window as unknown as { __lavascend: { debug: DebugBridge } }).__lavascend = { debug: bridge };
}
